/**
 * Google Fonts API
 * 
 * Google Fonts APIを使用してフォント一覧を取得し、
 * 動的にフォントをロードする機能を提供します。
 */

/**
 * フォント情報の型定義
 */
export interface GoogleFont {
  family: string;
  variants: string[];
  subsets: string[];
  category: string;
  isSystem?: boolean;
}

/**
 * フォントサブセットの型
 */
export type FontSubset = 'japanese' | 'latin';

/**
 * 人気日本語フォント TOP5（Google Fonts人気順）
 * APIキーがない場合や、API呼び出し前の初期表示に使用
 */
export const POPULAR_JAPANESE_FONTS: GoogleFont[] = [
  { family: 'Noto Sans JP', category: 'sans-serif', variants: ['400', '500', '700'], subsets: ['japanese'] },
  { family: 'M PLUS Rounded 1c', category: 'sans-serif', variants: ['400', '500', '700'], subsets: ['japanese'] },
  { family: 'M PLUS 1p', category: 'sans-serif', variants: ['400', '500', '700'], subsets: ['japanese'] },
  { family: 'Kosugi Maru', category: 'sans-serif', variants: ['400'], subsets: ['japanese'] },
  { family: 'Zen Kaku Gothic New', category: 'sans-serif', variants: ['400', '500', '700'], subsets: ['japanese'] },
];

/**
 * 人気英語フォント TOP5（Google Fonts人気順）
 * APIキーがない場合や、API呼び出し前の初期表示に使用
 */
export const POPULAR_LATIN_FONTS: GoogleFont[] = [
  { family: 'Roboto', category: 'sans-serif', variants: ['400', '500', '700'], subsets: ['latin'] },
  { family: 'Open Sans', category: 'sans-serif', variants: ['400', '500', '700'], subsets: ['latin'] },
  { family: 'Lato', category: 'sans-serif', variants: ['400', '700'], subsets: ['latin'] },
  { family: 'Montserrat', category: 'sans-serif', variants: ['400', '500', '700'], subsets: ['latin'] },
  { family: 'Poppins', category: 'sans-serif', variants: ['400', '500', '700'], subsets: ['latin'] },
];

/**
 * システムフォント（デフォルト）
 */
export const SYSTEM_FONT: GoogleFont = {
  family: 'System Default',
  category: 'sans-serif',
  variants: [],
  subsets: [],
  isSystem: true,
};

/**
 * システムフォントのCSS値
 */
export const SYSTEM_FONT_VALUE = "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif";

/**
 * Google Fonts APIからフォント一覧を取得
 * 
 * @param subset - フォントのサブセット（'japanese' または 'latin'）
 * @returns フォント一覧（プリセットを先頭に、API取得分をマージ）
 */
export const fetchGoogleFonts = async (subset: FontSubset): Promise<GoogleFont[]> => {
  const apiKey = import.meta.env.VITE_GOOGLE_FONTS_API_KEY;
  const preset = subset === 'japanese' ? POPULAR_JAPANESE_FONTS : POPULAR_LATIN_FONTS;

  if (!apiKey) {
    // APIキーがない場合はプリセットのみ返す
    console.warn('Google Fonts API key is not set. Using preset fonts only.');
    return preset;
  }

  try {
    const response = await fetch(
      `https://www.googleapis.com/webfonts/v1/webfonts?key=${apiKey}&subset=${subset}&sort=popularity`
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch fonts: ${response.status}`);
    }

    const data = await response.json();
    const apiFonts: GoogleFont[] = (data.items || []).map((item: any) => ({
      family: item.family,
      variants: item.variants || ['400'],
      subsets: item.subsets || [],
      category: item.category || 'sans-serif',
    }));

    // プリセットを先頭に、重複を除いてマージ
    const presetFamilies = new Set(preset.map(f => f.family));
    const uniqueApiFonts = apiFonts.filter(f => !presetFamilies.has(f.family));
    return [...preset, ...uniqueApiFonts];
  } catch (error) {
    // API失敗時はプリセットのみ返す
    console.warn('Failed to fetch Google Fonts:', error);
    return preset;
  }
};

/**
 * Google Fontsを動的にロード
 * 
 * @param font - ロードするフォント情報
 */
export const loadGoogleFont = (font: GoogleFont): void => {
  if (font.isSystem) {
    // システムフォントはロード不要
    return;
  }

  const encodedFamily = font.family.replace(/ /g, '+');
  
  // 既にロード済みかチェック
  const existingLink = document.querySelector(`link[href*="family=${encodedFamily}"]`);
  if (existingLink) {
    return;
  }

  // フォントウェイトを整形（数値のみ抽出）
  const weights = font.variants
    .map(v => v.replace(/[^0-9]/g, ''))
    .filter(v => v)
    .join(';') || '400;700';

  const link = document.createElement('link');
  link.href = `https://fonts.googleapis.com/css2?family=${encodedFamily}:wght@${weights}&display=swap`;
  link.rel = 'stylesheet';
  document.head.appendChild(link);
};

/**
 * CSS変数を更新してフォントを適用
 * 
 * @param font - 適用するフォント情報
 */
export const applyFont = (font: GoogleFont): void => {
  if (font.isSystem) {
    // システムフォントに戻す
    document.documentElement.style.setProperty('--font-family-main', SYSTEM_FONT_VALUE);
  } else {
    const value = `"${font.family}", ${font.category}`;
    document.documentElement.style.setProperty('--font-family-main', value);
  }
};

/**
 * フォント設定をlocalStorageに保存
 * 
 * @param font - 保存するフォント情報
 */
export const saveFontToStorage = (font: GoogleFont): void => {
  try {
    localStorage.setItem('selected-font', JSON.stringify(font));
  } catch (error) {
    console.warn('Failed to save font to localStorage:', error);
  }
};

/**
 * localStorageからフォント設定を読み込み
 * 
 * @returns 保存されているフォント情報、なければnull
 */
export const loadFontFromStorage = (): GoogleFont | null => {
  try {
    const stored = localStorage.getItem('selected-font');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to load font from localStorage:', error);
  }
  return null;
};
